/* eslint-disable max-len */
import React, { FC } from 'react'
import moment from 'moment-timezone'
import { Card } from 'transfix-ui/components/Card'
import { ICarrierApplication } from 'types'
import { StringParam, useQueryParams } from 'use-query-params'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { carrierEntities } from 'constants/entities'
import { useFindOnboardingMotorCarrierQuery } from '../carrier-search/graphql/FindOnboardingMotorCarrier'

interface IAgreement {
  ein: string
  signature: string
  entity: string
}
interface IAgreementTermsProps {
  agreement: IAgreement
  carrierDetails: ICarrierApplication
}

const CURRENT_DATE = moment().format(FORMATS.dateFormat)

// TODO, maybe move into /components in FP-2390
export const AgreementTerms: FC<IAgreementTermsProps> = ({ agreement, carrierDetails }) => {
  const [{ userType: userTypeName }] = useQueryParams({
    userType: StringParam,
  })
  const { ein, entity, signature } = agreement || {}
  const { dotNumber: dotNumberString, legalName, location, mcNumber } = carrierDetails || {}
  const { stateCode: secondSourceStateCode } = location || {}
  const matchingEntity = carrierEntities.find(type => type.key === entity)
  const entityDisplay = matchingEntity?.label || DISPLAY.emptyUnderline
  const einDisplay = ein || DISPLAY.emptyUnderline

  const dotNumber = Number(dotNumberString)

  const { data } = useFindOnboardingMotorCarrierQuery({
    variables: { dotNumber },
  })

  const { findOnboardingMotorCarrier } = data || {}
  const { addresses = [] } = findOnboardingMotorCarrier || {}

  const activeAddress = addresses?.find(({ isType }) => isType === 'physical')

  const { stateCode: highwayStateCode } = activeAddress || {}

  const stateCode = highwayStateCode || secondSourceStateCode

  return (
    <>
      <Card maxHeight>
        <p className='tc'>BROKER/MOTOR CARRIER AGREEMENT</p>
        <p>
          THIS BROKER/MOTOR CARRIER AGREEMENT (&#34;Agreement&#34;), made as of{' '}
          <strong className='underline'>{CURRENT_DATE}</strong> (&#34;Effective Date&#34;), by and
          between Transfix, Inc., a Delaware corporation having offices at 11 Park Place, 19th
          floor, New York, NY 10007-2804 (&#34;Transfix&#34;), and{' '}
          <strong className='underline'>{legalName}</strong>, a{' '}
          <strong className='underline'>{entityDisplay}</strong> organized in the State of{' '}
          <strong className='underline'>{stateCode}</strong> whose Federal Tax Identification number
          is <strong className='underline'>{einDisplay}</strong> and whose MC number is{' '}
          <strong className='underline'>{mcNumber}</strong> and USDOT number is{' '}
          <strong className='underline'>{dotNumber}</strong>, (hereinafter called the
          &#34;Carrier&#34;), (each a &#34;Party,&#34; collectively, the &#34;Parties&#34;).
        </p>
        <p>
          WHEREAS, Carrier is engaged in the business of transporting property, including non-exempt
          and exempt commodities, including produce, as defined in 49 U.S.C. §13506(6),
          (&#34;Commodities&#34;) and furnishing transportation services as a motor carrier in
          interstate and intrastate commerce;
        </p>
        <p>
          WHEREAS, Transfix is engaged in the business of arranging for transportation services of
          Commodities for compensation and desires to retain Carrier&#39;s services, on a
          non-exclusive basis, to satisfy a portion of its and its customers&#39;
          (&#34;Customers&#34;) transportation needs, as are further detailed in this Agreement;
        </p>
        <p>
          NOW THEREFORE, in consideration of the premises, mutual agreements and consideration set
          forth herein, the parties agree as follows:
        </p>
        <ol type='1'>
          <li>
            The term of this Agreement shall begin on the Effective Date and shall remain in effect
            for a period of one (1) year and shall automatically renew from year-to-year on its
            anniversary date unless sooner terminated as provided for herein (collectively, the
            &#34;Term&#34;). This Agreement may be terminated by either Party by its providing the
            other Party not less than thirty (30) days prior written notice
          </li>
          <li>
            Carrier represents and warrants that it is and shall at all times during the Term be
            registered as a motor carrier with the Federal Motor Carrier Safety Administration
            (&#34;FMCSA&#34;), and/or any other applicable Federal, State or local agency and holds
            requisite authority to lawfully furnish all of the services provided for herein
            (&#34;Operating Authority&#34;). Carrier agrees to provide Transfix with copies of all
            Federal, State, local and/or foreign operating authorities, permits, and/or
            authorizations to provide such services upon request.
          </li>
          <li>
            Transfix may from, time-to-time, tender shipments of Commodities to Carrier during the
            Term. For purposes of this Agreement, &#34;Tender&#34; shall be defined as notification
            by Transfix to Carrier of any shipment offered by Transfix for transportation by
            carrier. Carrier agrees, based on its capacity and workload requirements, to accept such
            shipments, transmit a signed copy of Transfix&#39;s &#34;Load Confirmation&#34; form to
            Transfix via e-mail or facsimile or such other method of communication agreed to in
            writing by the Parties as confirmation of its acceptance to transport and deliver each
            shipment safely, efficiently, and with reasonable dispatch. Neither this Agreement to
            Tender shipments, nor the Tender of any such shipments, shall under any circumstances be
            deemed to constitute a practice or course of conduct, or as a minimum or maximum
            guarantee of the number of shipments to be Tendered or of any revenue or profit Carrier
            might attain. It is understood that this is a non-exclusive Agreement, and that Transfix
            shall be free to utilize services of carriers other than Carrier, and that Carrier shall
            be free to accept freight for transportation from entities other than Transfix.
          </li>
          <li>
            All shipments transported by Carrier on or after the Effective Date shall be subject
            solely to the terms and conditions of this Agreement and any non-conflicting provisions
            of Federal law applicable to the services. In the event any Commodities transported
            hereunder are not deemed exempt under 49 U.S.C. §13506(a)(6), then all rights and
            remedies found in Part B, 49 U.S.C., Subtitle IV, that conflict with the terms and
            conditions of this Agreement are hereby waived by Carrier and Transfix, including, if
            applicable, pursuant to 49 U.S.C. §14101(b), and the presence of specific waivers
            elsewhere in this Agreement shall not be construed to narrow the scope of the foregoing
            general waiver.
          </li>
          <li>
            Carrier shall issue and sign a freight receipt or bill of lading for each shipment it
            transports. Upon notice, Carrier will provide Transfix with copies of such receipts or
            bills of lading at no cost to Transfix. If Transfix, Carrier or any third party elect to
            utilize bills of lading, or freight receipts, or other forms of shipping documents,
            contracts or agreements (&#34;Documents&#34;) for any shipment covered by this
            Agreement, any terms, conditions or provisions of such Documents shall be deemed null
            and void and the Documents will act solely as a proof-of-receipt and/or delivery, as
            applicable. Irrespective of the foregoing, any instructions regarding Commodities
            provided by Transfix shippers or consignees found on bill of lading, freight receipt, or
            Load Confirmation shall be binding upon carrier.
          </li>
          <li>
            <ol type='a'>
              <li>
                Carrier, at its sole cost and expense, shall furnish all equipment, supplies and
                services necessary for it to provide the services hereunder, and shall maintain all
                equipment it utilizes in good order and condition in compliance with reasonable
                industry standards and practices, manufacturers requirements and the Federal Motor
                Carrier Safety Regulations (&#34;FMCSR&#34;). Carrier, on an ongoing basis, will
                provide Transfix with copies of all certifications, permits, findings or notices of
                any kind issued by the FMCSA or other applicable Federal, State, or local agency,
                verifying that Carrier meets all registration, quality, safety, insurance and
                maintenance requirements (&#34;Permits&#34;) upon request. Carrier shall immediately
                upon becoming aware of such condition, or the possibility of such condition
                occurring, notify Transfix of the change in termination, expiration, cancellation,
                suspension, revocation or modification of its Operating Authority, Permit, and/or
                insurance, whether required hereunder or pursuant to applicable statutes or
                regulations. Transfix and its Customers shall have no responsibility or liability
                for any equipment owned, leased or utilized by Carrier, and Carrier agrees to
                indemnify, defend and hold Transfix harmless from and against any and all Claims, as
                hereinafter defined related to or regarding such equipment, or the use thereof.
              </li>
              <li>
                Upon being rated, Carrier shall at all times during the Term, maintain a FMCSA
                safety rating of &#34;Satisfactory,&#34; or &#34;Un-rated&#34; and will notify
                Transfix immediately in writing if its safety rating is changed to
                &#34;Conditional&#34; or &#34;Unsatisfactory.&#34; Carrier represents that it will
                at all times during the Term operate without FMCSA intervention or restriction.
                Carrier shall endeavor to maintain a satisfactory U.S. DOT Safety Rating but under
                no circumstances is Carrier allowed to provide services under this contract if their
                safety rating falls to &#34;unsatisfactory.&#34; Further, at the request of
                Transfix, the carrier will present a &#34;Safety Letter&#34; for its safety rating.
              </li>
              <li>
                Carrier, at its sole cost and expense, shall employ for its services hereunder only
                competent, able, experienced, fully trained and legally licensed personnel,
                including, without limitation, drivers holding a single valid Commercial Drivers
                Licenses from their State of residence. Carrier has sole responsibility for the acts
                and omissions of its employees, agents, owner-operators, and any other persons or
                entities fulfilling any of Carrier&#39;s obligations under this Agreement, and such
                persons or entities shall at no time and for no purpose be deemed to be under the
                control of Transfix, or the joint control of both Parties.
              </li>
              <li>
                Carrier shall not cause or permit any shipment tendered hereunder to be transported
                by any other motor carrier, via use of a broker or freight forwarder, including
                itself as such, or in substituted service, without the express prior written consent
                of Transfix. In the event Carrier violates this Section 6(c), it shall remain solely
                liable for the shipments in question and for all obligations under this Agreement as
                if it provided the services utilizing its own equipment and personnel. If Carrier
                breaches this provision, Broker shall have the right of paying the monies it owes
                Carrier directly to the delivering Carrier, in lieu of payment to carrier. Upon
                Broker&#39;s payment to delivering Carrier, Carrier shall not be released from any
                liability to Broker under this agreement. In addition to the indemnity obligation
                reflected in this agreement the Carrier will be liable for consequential damages for
                violation of this clause of the agreement.
              </li>
              <li>
                Carrier hereby authorizes Transfix to directly invoice Customers for all freight,
                accessorial or other charges claimed due from such entities as agent for and on
                behalf of Carrier solely for this purpose. Payment of said charges to Transfix by
                Customers shall be construed as payment to Carrier as if collected directly by
                Carrier, and shall relieve such Customers from all responsibility or liability, at
                law or in equity, to Carrier for the payment or non-payment of freight charges.
                Carrier&#39;s sole remedy shall be to seek payment from Transfix.
              </li>
              <li>
                In the event of an alleged or actual product contamination issue or claim or
                investigation regarding same, i.e., e-coli, Carrier shall make any trailer Transfix
                or its Customer deems relevant to the issue, claim or investigation available for
                inspection by Transfix, its Customer, and/or their agents, contractors or
                representatives, within a reasonable timeframe of Transfix&#39;s request for such
                inspection, and shall take no action which will alter the condition of the trailer
                subsequent to the notice from Transfix. Carrier shall also provide Transfix with all
                records and documents relating to the subject trailer&#39;s use for a period of
                thirty (30) days prior to its service to Transfix&#39;s Customer, including, without
                limitation, documents identifying products transported, trailer cleaning, shippers
                and consignees.
              </li>
            </ol>
          </li>
          <li>
            As full and complete compensation for the services provided by Carrier hereunder,
            Transfix shall pay Carrier solely in accordance with the rates, charges, rules and
            regulations set forth or referenced in Appendix 1, attached hereto. Carrier waives the
            applicability of any rates, rules, charges, practices or classNameifications contained
            in its own tariffs or other publications, or those to which it is or might be a party.
            Except for &#34;spot market&#34; rates, as provided for in Appendix 1, Part 1(2),
            attached hereto, all revisions of Appendix 1, to be effective, must be done in
            compliance with Section 22 herein.
          </li>
          <li>
            <ol type='a'>
              <li>
                Carrier shall provide the original signed proof of delivery (&#34;POD&#34;) within
                thirty (30) days of the delivery date (as specified on the POD), or as otherwise
                stated on the rate confirmation. Transfix shall make payment to Carrier for
                Carrier&#39;s services hereunder within thirty (30) days of its receipt of
                Carrier&#39;s timely reasonably detailed invoice (&#34;Invoice&#34;) and signed
                original proof-of-delivery (&#34;POD&#34;) for each and every shipment referenced on
                such Invoice at its offices with attention to Accounts Payable Department. Carrier
                agrees that Transfix shall have no payment obligation until a proper Invoice and all
                relevant POD&#39;s have been provided to Transfix by Carrier and further agrees that
                Transfix shall not render any payment to Carrier if the POD is not received by
                Transfix within such thirty (30) day period, or as otherwise stated on the rate
                confirmation. Payment of Carrier&#39;s Invoices by Transfix shall not waive any
                right of Transfix to contest the validity of any Invoice, or parts thereof, for any
                reason, equitable principle, and/or rule of law. In the event Transfix disputes any
                portion of any Invoice, it shall notify Carrier of such dispute prior to the payment
                due date, and make payment to Carrier for the undisputed portion of such Invoice.
                The Parties shall thereafter work expeditiously and in good faith to resolve the
                dispute. The Parties expressly waive 49 U.S.C. 13707(a) and (b) as it relates to
                credit terms, where such statutory section(s) would otherwise be applicable.
              </li>
              <li>
                Carrier shall notify Transfix in writing within sixty (60) days of its receipt of
                payment from Transfix of any payment omissions or errors, including, without
                limitation underpayment. Failure by Carrier to provide such notification shall be
                deemed to be the waiver of its right to collect any monies due and owing to it
                relating to or caused by Transfix&#39;s omissions or errors. The Parties expressly
                waive 49 U.S.C. 137l0(a)(3)(A), as it relates to a Carriers right to collect
                charges, where such statutory section would otherwise be applicable. In addition,
                the provisions of this Section 8 shall survive the expiration or termination of this
                Agreement.
              </li>
            </ol>
          </li>
          <li>
            Carrier shall have no lien on any shipment, or portion thereof, transported under this
            Agreement, and hereby knowingly waives its right(s) to any and all liens which may rise
            by operation of law or in equity upon any shipment or portion thereof. In addition,
            Carrier shall take no action to place or assist in the placement of any lien or
            encumbrance on any shipment hereunder, or part thereof, while they are in Carrier&#39;s
            possession, care, custody and/or control.
          </li>
          <li>
            <ol type='a'>
              <li>
                Carrier shall maintain in force during the Term, insurance policies of the types and
                in at least the minimum amounts listed below, to be written by insurance companies
                with a minimum A.M. Best rating of A-,must be authorized to do business under the
                laws of the state(s) or province(s) in which Carrier provides the transportation and
                related services as specified in load confirmation communications received from
                Broker, And naming Carrier as named insured and Transfix by endorsement as
                additional insured on the liability policies as its interests bear herein, and loss
                payee (for cargo) and meeting the requirements of all regulatory bodies having
                jurisdiction over carrier. All insurance referenced herein shall be written on an
                occurrence basis and to act and respond to any loss covered on a primary and
                non-contributory basis to any coverage of Transfix or Customers. Carrier shall
                furnish to Transfix original, properly executed, Certificate(s) of Insurance from
                its broker or insurer, within five (5) days of the Effective Date evidencing: (i)
                insurer(s) affording coverage ; (ii) the coverage; (iii) effective and expiration
                dates of policies; (iv) that Transfix will be given a minimum of thirty (30)
                days&#39; notice of any cancellation, non-renewal, or material change in any
                coverage; (v) that a contractual liability endorsement has been included in the
                commercial general liability policy; (vi) any deductible and/or self-insured
                retention; (vii) any exclusion to policies which are not part of the standard form;
                (viii) Transfix by endorsement as an additional insured as its interest may appear
                on the liability policies and loss payee for cargo, and provide the actual
                endorsements to policy(s) within 30 days of the original request ; and (ix) that
                Transfix shall not be obligated to pay premiums or deductibles for any of
                Carrier&#39;s insurance. In addition, Carrier&#39;s insurance policies may not have
                exclusions for reefer breakdown or unattended vehicle, or Carrier must maintain
                separate coverage for same.
                <table className='plain'>
                  <thead>
                    <tr>
                      <th aria-label='Coverage Details' className='quarter-width'>
                        &nbsp;
                      </th>
                      <th>COVERAGE</th>
                      <th>MINIMUM LIMITS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>i.</td>
                      <td className='underline'>Worker&#39;s Compensation</td>
                      <td>Statutory</td>
                    </tr>
                    <tr>
                      <td>ii.</td>
                      <td className='underline'>Employers Liability</td>
                      <td>$1,000,000 each incident, disease or accident</td>
                    </tr>
                    <tr>
                      <td>iii.</td>
                      <td>
                        <span className='underline'>Commercial General Liability </span> Including
                        premises, completed operations, independent contractors, personal injury,
                        and broad form contractual
                      </td>
                      <td>
                        $1,000,000 each occurrence, combined single limit, bodily injury and
                        property damage
                      </td>
                    </tr>
                    <tr>
                      <td aria-label='Coverage Details' />
                      <td colSpan={2}>
                        This policy must include coverage for the liability assumed by the Carrier
                        under the indemnity provisions of the Agreement.
                      </td>
                    </tr>
                    <tr>
                      <td>iv.</td>
                      <td className='underline'>
                        Employee Theft and Dishonesty or Crime (or bond)
                      </td>
                      <td>$100,000 per incident or occurrence</td>
                    </tr>
                    <tr>
                      <td>v.</td>
                      <td className='underline'>Automobile Liability</td>
                      <td>
                        $1,000,000. each accident, combined single limit, bodily injury and property
                        damage.
                      </td>
                    </tr>
                    <tr>
                      <td aria-label='Coverage Details' />
                      <td colSpan={2}>
                        This policy will cover all owned, non-owned and hired vehicles of carrier.
                        If the policy is for &#34;Scheduled Auto&#34;, then carrier must supply a
                        vin schedule and have Hired and Non-owned coverage. Coverage will also
                        comply with all applicable automobile statutes and no fault laws.
                      </td>
                    </tr>
                    <tr>
                      <td>vi.</td>
                      <td className='underline'>
                        Umbrella Liability with (ii), (iii) and (v) as scheduled underlying
                        coverage)
                      </td>
                      <td>
                        $1,000,000. each occurrence, combined single limit, bodily injury and
                        property damage.
                      </td>
                    </tr>
                    <tr>
                      <td>vii.</td>
                      <td>
                        <span className='underline'>Cargo Insurance </span> (with no exclusion for
                        mechanical, including reefer, breakdown or unattended vehicle)
                      </td>
                      <td>$100,000. per occurrence</td>
                    </tr>
                    <tr>
                      <td>viii.</td>
                      <td>
                        <span className='underline'>
                          All Risk Physical Damage Comprehensive and Collision
                        </span>
                      </td>
                      <td>
                        Actual cash value of the trailers leased to, operated, maintained and/or
                        used by carrier.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </li>
              <li>
                Transfix&#39;s failure to object to any lack of coverage, or to deficiencies in such
                coverage, shall not bar any claim by Transfix for indemnity, or limit or waive
                Carrier&#39;s obligation to secure any coverage set out in this Agreement, or
                required under any applicable law or regulation. Carrier acknowledges that it fully
                understands that Transfix does not represent that the types of coverage and/or
                coverage minimums set forth herein are adequate to protect Transfix&#39;s or
                Customers interests, nor do they act to in any way limit or waive Carrier&#39;s
                liability hereunder. In addition, any premiums and deductible amounts under the
                policies described herein shall be paid solely by carrier.
              </li>
              <li>
                If Carrier maintains any level of self-insurance, it must provide Transfix with
                evidence of such coverage, including a copy of a valid Authorization to be a
                Self-Insurer from the FMCSA or other applicable governing agency or body. Carrier
                shall not insure through a Risk Retention Group, or maintain a self-insured
                retention without the prior written approval of Transfix.
              </li>
              <li>
                The failure, refusal to pay or denial of any claim by Carrier&#39;s insurer shall
                not be a valid reason for denial of any claim by carrier.
              </li>
              <li>
                Carrier may maintain liability coverage through a combination of primary and
                umbrella coverage so long as all policies are written to eliminate all gaps in
                coverage.
              </li>
              <li>
                All insurance policies shall provide for a waiver of the underwriter&#39;s
                subrogation rights against the Indemnitees and Carrier waives any subrogation rights
                it has or might have against Indemnitees.
              </li>
            </ol>
          </li>
          <li>
            <ol type='a'>
              <li>
                If any shipment, or part thereof, is (i) lost, damaged, or not delivered or
                delivered late to its assigned consignee, or (ii) is refused based upon quality or
                defect by United States Department of Agriculture (exempt conditions only) or
                consignee quality assurance inspectors upon delivery, (iii) or is unreasonably
                delayed effecting monetary loss upon Transfix or its Customer (individually and
                collectively &#34;Exceptions&#34;), then Carrier shall pay Transfix, as
                representative of Customer, for the full value of the goods as if Transfix were the
                owner or beneficial owner of the goods. For the purposes of this Agreement,
                &#34;full value of the goods&#34; is defined as amount paid by Transfix to its
                customer for the subject Exception, with a maximum of $100,000.00 per trailer unless
                otherwise agreed to by the Parties in writing.
              </li>
              <li>
                Transfix, on behalf of Customers, shall submit a reasonably detailed written claim
                to Carrier for loss, damage, non-delivery, product quality or lateness of any
                shipment within nine (9) months after delivery of such shipment, or if non-delivery
                of any shipment occurs, after reasonable time for such delivery has elapsed. Carrier
                shall acknowledge all claims in writing within seven (7) business days of its
                receipt of same, and either make payment or decline such claims within ninety (90)
                days of receipt, any declination to include a reasonably detailed explanation of
                Carrier&#39;s legally cognizable reason for such action. In the event Carrier fails
                to either make payment or properly decline any claim within said ninety (90) day
                period, then Carrier agrees that Transfix shall have the right to deduct the amount
                of such claim from any monies due to Carrier from Transfix, and waives any
                objections or defenses it might have thereto.
              </li>
              <li>
                Any action at law to recover for loss, damage or non-delivery shall be instituted by
                Transfix or its Customers against Carrier not later than two (2) years after written
                declination of claim has been delivered to Transfix. The Parties waive any and all
                rules and regulations found in 49 U.S.C. 14706 or in 49 C.F.R., Part 370 which
                conflict with this Section 11, where such rules and regulations would otherwise be
                applicable.
              </li>
              <li>
                Carrier&#39;s insurers denial or refusal to pay any claim shall not be considered a
                proper denial by Carrier or reason for Carrier to provide a denial.
              </li>
              <li>
                Carrier shall be responsible for the loss or damage to the property of others
                occurring during loading or unloading by its personnel.
              </li>
              <li>
                Carrier shall not agree to any limitation on liability for loss, damages or
                non-delivery, other than as is specifically set forth in this Agreement, and any
                such limitation not expressly agreed to by Transfix, including, without limitation,
                if found in any Document, shall be null and void. To the extent Carrier
                inadvertently or otherwise transports shipments which are later deemed to be subject
                to a limitation, Carrier shall nonetheless be liable to Transfix for the full value
                of the goods, as defined in this Agreement, irrespective of such limitation.
              </li>
              <li>
                The provisions of this Section 11 shall survive the expiration or termination of
                this Agreement.
              </li>
            </ol>
          </li>
          <li>
            <ol type='a'>
              <li>
                Any claim by Carrier to recover charges in addition to those originally invoiced
                shall be commenced not more than one hundred and eighty (180) days after
                Transfix&#39;s receipt of the original invoice with respect to which such charge is
                claimed due. Expiration of said one hundred and eighty (180) day period shall be a
                complete and absolute defense against any such claim, regardless of any extenuating
                or mitigating circumstances of any nature.
              </li>
              <li>
                Any action at law by either Party to collect alleged undercharges or overcharges
                shall be commenced by Carrier or Transfix, respectively, not later than two (2)
                years after delivery of the shipment. Expiration of said two (2) year term shall be
                a complete and absolute defense against any such claim, regardless of any
                extenuating or mitigating circumstances of any nature.
              </li>
              <li>
                Carrier and Transfix expressly waive the filing periods found in 49 U.S.C. 14705,
                where such filing periods would otherwise be applicable. In addition, the provisions
                of this Section 12 shall survive the expiration or termination of this Agreement.
              </li>
            </ol>
          </li>
          <li>
            In addition to, and without limiting or waiving any other indemnity provisions set forth
            in this Agreement, Carrier agrees to indemnify, defend, and hold Transfix and its
            principals, directors, officers, employees, subsidiaries, affiliates and Customers
            (&#34;Indemnitees&#34;) harmless from and against any and all losses, actions,
            judgments, fines, damages, costs and expenses, including reasonable attorneys&#39;s fees
            and court costs and claims brought by any person or entity (&#34;Claims&#34;),
            including, without limitation, Carrier&#39;s employees, agents, invitees and/or
            contractors, including, without limitation, for injury or death of persons and/or damage
            to property, arising out of, resulting from or in connection with (i) the services to be
            performed or provided by Carrier under this Agreement, (ii) Carriers breach or default
            hereunder, (iii) Carriers&#39;s misrepresentation of any facts or claims it implicitly
            or expressly makes or references in this Agreement or otherwise, (iv) Carriers failure
            to comply with any and all applicable Federal, State, or local laws and regulations of
            the respective regulatory bodies having jurisdiction over its operations, or (v) claims
            by Carriers&#39;s employees or any governmental authority that such employees are
            employees of Transfix or are entitled to salary or benefits through Transfix.
            Irrespective of the foregoing, Carrier shall have no responsibility to indemnify any
            Indemnitee to the extent any claim is caused by such Indemnitee&#39;s negligence,
            willful misconduct or violation of law. The provisions of this Section 13 shall survive
            the expiration or termination of this Agreement.
          </li>
          <li>
            Carrier represents and warrants that it: (i) shall, at its sole expense, procure and
            maintain all licenses, regulations, authorizations, and permits required for its
            business and operations and the services to be provided hereunder, (ii) shall comply
            with all applicable Federal, State and local laws, ordinances and regulations pertaining
            thereto, including, without limitation, those of the United States Food and Drug
            Administration, including, without limitation, the Food Safety Modernization Act, the
            United States Department of Agriculture, the FMCSA, the United States Environmental
            Protection Administration and the United States Department of Homeland Security; (iii)
            shall make payment when due for all taxes or assessments applicable to Carrier or its
            employees or business, including, without limitation, all taxes and assessments payable
            under Federal and State Unemployment Insurance Laws and the Federal Insurance
            Contributions Act, and highway, road use and fuel taxes; and (iv) is an Equal Employment
            Opportunity Employer.
          </li>
          <li>
            Carrier shall perform any and all services hereunder as an independent contractor.
            Transfix neither exercises nor retains any control or supervision of or over Carrier,
            and/or its operations, equipment, employees, contractors and/or agents, and no other
            relationship including, without limitation, employer/employee, parent/subsidiary,
            affiliates, principal/agent, fiduciary, partnership, joint venturers and/or
            employer/employee exists or shall be construed to exist between Transfix and Carrier for
            any reason and/or purpose whatsoever. Carrier shall have no authority to, nor shall it
            act in any way to bind Transfix or represent to any person or entity that it has the
            authority to do so and Transfix shall at no time be a guarantor or warrantor of any
            debts or obligations of carrier.
          </li>
          <li>
            All notices required or permitted under this Agreement shall be in writing, and shall be
            sent postage prepaid via the United States Postal Service (&#34;USPS&#34;), by
            registered or certified mail, return receipt requested, or nationally recognized
            overnight courier, i.e., Federal Express, to the Parties&#39; addresses first set forth
            above, or to such other address as a party shall in writing request, and shall be deemed
            given one (1) business day after being deposited with the USPS or such courier. Notices
            to Transfix should be directed to its President. Notices to Carrier should be directed
            to the Carrier representative who signed this Agreement.
          </li>
          <li>
            Neither Party shall be liable to the other Party for the failure on its part to perform
            any of the terms or provisions of the Agreement, that are a result of any cause not
            within its control (&#34;Event of Force Majeure&#34;), including without limitation,
            Acts of God, wars, revolutions, civil disturbances or other disorders, strikes,
            ordinances, laws or governmental action. If, as a result of an Event of Force Majeure,
            Carrier shall fail to perform its services under this Agreement, Transfix shall be
            entitled during the period of non-performance to employ for the services one or more
            third parties to complete or perform such services, Carrier shall fully cooperate with
            such third parties, and Transfix&#39;s sole obligation to Carrier shall be to pay
            Carrier for any services performed. In the event an Event of Force Majeure shall last
            for a period of thirty (30) days or more, Transfix may terminate this Agreement without
            any further obligation to Carrier, except for payment of valid charges due to Carrier
            for services provided prior to the date of termination.
          </li>
          <li>
            If any of the following shall occur, Transfix may, without prejudice to any other right
            or remedy, terminate this Agreement by providing Carrier with not less than seven (7)
            days written notice if (i) in the opinion of Transfix, the financial responsibility of
            Carrier is impaired; (ii) Carrier should repeatedly refuse or fail to perform any duty,
            obligation, or responsibility required by the terms of this Agreement, or disregard laws
            or regulations applicable to its performance under the terms of this Agreement; and/or
            (iii) Carrier otherwise commits a material violation of any provision of this Agreement.
            In addition, Transfix may immediately terminate this Agreement in the event
            Carrier&#39;s Operating Authority, Permits or any insurance required hereunder is
            cancelled, terminated, suspended, revoked or materially altered. In the event notice of
            termination is provided by either Party pursuant to this Agreement, the Parties shall
            maintain their rights and responsibilities during the applicable notice period.
          </li>
          <li>
            The waiver of any breach of, or default under, or the failure to enforce any provision
            of this Agreement shall not be deemed as a waiver of any subsequent breach or default.
            The rights and obligations contained in this Agreement are separate and divisible, and
            in the event that any Section or portion thereof is deemed unenforceable by a court or
            administrative agency of proper jurisdiction, then the balance of this Agreement shall
            continue in full force and effect. This Agreement, or any right hereunder, shall not be
            assigned by Carrier without the express written consent of Transfix. The expiration or
            termination of this Agreement shall not affect the Sections and the rights and
            obligations set forth herein which (i) state or evidence the intent of the Parties that
            the provisions survive, or (ii) must survive to give effect to the provisions thereof.
          </li>
          <li>
            <ol type='a'>
              <li>
                Except as required by law, or in response to a valid subpoena, the terms and
                conditions of this Agreement, and any information pertaining to any shipment
                hereunder, shall not be disclosed by either party hereto to persons or entities
                other than its directors, officers, employees, agents, attorneys, accountants and
                auditors on a need-to-know basis. The terms of this Section shall include, without
                limitation, disclosure of shipment and routing information as defined in 49 U.S.C. §
                14908(a)(1), which is deemed to be at all times applicable to this Agreement. In
                addition, Carrier shall obtain the express written consent of Transfix prior to
                releasing any publicity or other information regarding the existence or content of
                this Agreement or the Parties&#39; business relationship.
              </li>
              <li>
                Carrier agrees that any and all information and/or data of any type of kind provided
                or transmitted to Carrier by Transfix prior to or during the Term, whether verbally,
                electronically, in print, or otherwise (&#34;Data&#34;), shall remain the sole
                property of Transfix, and is provided to Carrier for the sole purpose of use in
                performing its obligations hereunder. Carrier fully understands that such Data is
                confidential and proprietary to Transfix, and that it shall have no rights of
                ownership, use or disclosure, and have a license of any type or kind, whether
                express or implied, to utilize such Data, except as is expressly set forth herein,
                and Carrier knowingly waives any and all rights of any type or kind it has, had or
                might have related to the Data or its use. In the event Carrier receives any private
                or governmental request(s), whether by subpoena or in any other manner, for any
                Data, work papers, reports or any other information, whether written or oral,
                relating to this Agreement or to Transfix, then Carrier shall immediately notify
                Transfix, and Transfix shall have the right to participate in Carrier&#39;s response
                to any such request by seeking to quash, modify or otherwise respond to any such
                request.
              </li>
              <li>
                Carrier shall have no right or license, whether express or implied, to utilize the
                trademarks, copyrights or other intellectual property of Transfix or its Customers
                without the express written consent of Transfix or the applicable Customer, and then
                only in the manner and medium so permitted.
              </li>
            </ol>
          </li>
          <li>
            The Parties agree not to (i) solicit for hire or hire any of the other Party&#39;s
            employees, and (ii) solicit business from Customers on lanes tendered to Carrier by
            Transfix during the Term, and for a period of twelve (12) months from the expiration or
            termination of this Agreement, unless otherwise expressly agreed to in writing by the
            other Party or Transfix, as applicable. Carrier agrees that in the event it violates
            Section 21 that it shall pay to Transfix an amount equal to 10% of its linehaul charges
            without limiting any other right of Transfix at law or in equity.
          </li>
          <li>
            <ol type='a'>
              <li>
                This Agreement, together with the attached Exhibits and Appendices, constitutes the
                complete and exclusive agreement between the Parties on the subject matter herein,
                and supersedes any and all prior and/or contemporaneous promises, contracts or
                agreements, whether oral or in writing, in regard to such subject matter. This
                Agreement may not be modified or amended unless such modifications or amendments are
                made in writing and signed and dated by both Parties.
              </li>
              <li>
                In addition to and without limiting Carrier&#39;s obligations under Section 22(a),
                Carrier represents and warrants that in the event it provides services in or through
                California hereunder, that its equipment shall meet all applicable standards,
                requirements and regulations of the California Environmental Protection Agency Air
                Resources Board (&#34;CARB&#34;), including, without limitation, all CARB in-use
                requirements for transport refrigeration units (&#34;TRU&#39;s&#34;).
              </li>
            </ol>
          </li>
          <li>
            Any rights prescribed in the Agreement are cumulative and are not intended to be
            exclusive of any other remedies to which an injured party may be entitled at law or
            equity in case of any breach or threatened breach by the other Party of any provision of
            this Agreement, unless such other remedies which are not prescribed in this Agreement
            are specifically limited or excluded by this Agreement. The use of one or more available
            remedies shall not bar the use of any other remedy for the purpose of enforcing the
            provisions of this Agreement; provided, however, that a Party shall not be entitled to
            retain the benefit of inconsistent remedies.
          </li>
          <li>
            Prior to the commencement of litigation, the Parties will attempt in good faith to
            resolve any controversy promptly by negotiation between their respective executives (if
            appropriate, with their respective counsel).
          </li>
          <li>
            THE PARTIES AGREE THAT THIS AGREEMENT SHALL BE DEEMED TO HAVE BEEN EXECUTED IN, AND
            SHALL BE CONSTRUED SOLELY UNDER APPLICABLE FEDERAL LAW AND THE LAWS OF THE STATE OF NEW
            YORK, WITHOUT RESPECT TO CONFLICT OF LAW PRINCIPLES. THE PARTIES FURTHER AGREE THAT ALL
            CLAIMS, ACTIONS AND DISPUTES ARISING IN CONNECTION WITH THIS AGREEMENT SHALL BE
            ADJUDICATED EXCLUSIVELY IN THE SUPREME COURT OF NEW YORK FOR NEW YORK COUNTY
            (MANHATTAN), OR UNITED STATES DISTRICT COURT FOR THE SOUTHERN DISTRICT OF NEW YORK, AND
            CONSENT TO THE PERSONAL AND SUBJECT MATTER JURISDICTION OF SUCH COURTS, ACKNOWLEDGE THAT
            VENUE IS PROPER THEREIN AND WAIVE ANY AND ALL OBJECTIONS THERETO, INCLUDING, WITHOUT
            LIMITATION, THAT OF INCONVENIENT FORUM. THE PARTIES FURTHER WAIVE THEIR RIGHTS TO A JURY
            TRIAL TO THE FULLEST EXTENT ALLOWED UNDER APPLICABLE LAW.
          </li>
          <li>
            Each Party represents and warrants to the other party that (i) it has the corporate
            power and authority to execute and deliver this Agreement and consummate the
            transactions contemplated hereby; and (ii) this Agreement has been executed and
            delivered by it and constitutes its valid and binding agreement, enforceable against it
            in accordance with its terms. Each Party further represents and warrants that neither
            the execution, delivery or performance of this Agreement, nor the performance of the
            services described therein, will result in any breach or violation of its Certification
            of Incorporation or By-Laws, or result in any default under the terms of any agreement
            or other contract to which it is a party, or by which it is otherwise bound.
          </li>
          <li>
            This Agreement may be executed in counterparts, each of which shall be an original, and
            all of which shall be deemed one and the same document.
          </li>
        </ol>
        <p>
          IN WITNESS WHEREOF, the Parties hereto have caused the Agreement to be signed by their
          duly authorized officers as of the Effective Date.
        </p>
        <div className='row'>
          <div className='col-xs-12 col-md-4 md-shift-4'>
            <p>TRANSFIX, INC.</p>
            <p>By:</p>
            <p>Name:</p>
            <p>Title:</p>
            <p>CARRIER</p>
            <p>
              By: <span className='signature'>{signature}</span>
            </p>
            <p>Name: {signature} </p>
            <p>Title: {userTypeName}</p>
          </div>
        </div>
        <p className='tc'>APPENDIX 1</p>
        <p className='underline'>RATES, CHARGES, RULES AND REGULATIONS</p>
        <p>
          <strong>
            <span className='underline'>Part 1</span>. Rates and Charges.
          </strong>
        </p>
        <ol type='1'>
          <li>Rates as agreed to in rate confirmation and tender documents.</li>
          <li>
            Irrespective of the provisions of Section 7 of the Agreement, in the event the Parties
            utilize &#34;spot market&#34; pricing for any shipments, Transfix shall transmit a
            confirmation memo to Carrier verifying the rate agreed to (&#34;Confirmation&#34;) and
            maintain a copy of such Confirmation and proof of transmission in its files. In all
            instances, the Confirmation shall (i) contain the only legal, lawful and applicable rate
            for the shipment(s) it relates to, and (ii) be considered as conclusive evidence of the
            rate(s) agreed to by the Parties in writing.
          </li>
        </ol>
        <p>Notes</p>
        <ol type='i'>
          <li>All rates shall be on a per trailer basis, unless otherwise specified.</li>
          <li>
            Where applicable, mileage will be calculated by determining the distance between the
            initial pick-up point(s) and the final destination point using PCMiler, Version 27
            (Practical Miles) or other system mutually agreed to by the Parties.
          </li>
          <li>
            Unless otherwise specifically and expressly set forth herein, all rates reflect and
            include charges for driver loading and unloading services, including, without
            limitation, the sorting and segregating of product and palletization to Customers or
            designated consignee&#39;s specifications at destination. Transfix shall not be subject
            to any surcharges or accessorial charges, except for those which are specifically and
            expressly set forth in Appendix 2.
          </li>
          <li>Be advised that advances may incur a two percent (2%) fee.</li>
        </ol>
        <p>
          <strong>
            <span className='underline'>Part 2</span>. Rules and Regulations.
          </strong>
        </p>
        <ol type='1'>
          <li>
            Unless otherwise specified, all deliveries shall be made on an appointment only basis.
          </li>
          <li>
            Transfix shall provide Carrier with freight availability and delivery dates. Carrier is
            responsible for confirming actual pick-up and delivery appointments with applicable
            origin, destination and intermediate, warehouses and distribution centers.
          </li>
          <li>
            <ol type='a'>
              <li>
                Carrier represents and warrants that all employees, contractors, and agents
                (&#34;Personnel&#34;) utilized by it under this Agreement to load or unload trailers
                with the aid of powered industrial equipment meet the OSHA training standards for
                the use of such equipment set forth at 29 U.S.C. §1910.178(1).
              </li>
              <li>
                Carrier, at its sole discretion, may choose to employ helpers, contractors,
                &#34;lumpers,&#34; or other persons or entities to assist in the loading or
                unloading of its trailers hereunder. Carrier hereby represents and agrees that such
                persons or entities shall be deemed solely as its agents for all purposes under this
                Agreement, and shall meet all relevant criteria set out in this Agreement.
              </li>
            </ol>
          </li>
          <li>
            Carrier shall notify Transfix on a daily basis by 10:00 AM Eastern Standard Time via
            facsimile, telephone, EDI, E-Mail, or an agreed upon website, if available, of shipment
            status.
          </li>
          <li>
            <ol type='a'>
              <li>
                It shall be Carrier&#39;s sole responsibility to load and/or inspect all cargo at
                origin as it is being loaded, other than where the shipment is deemed &#34;Shippers
                Load and Count&#34; as defined in Appendix 1, Part 2, Paragraph 6, to assure (i) the
                freight is in good order and condition without defect, (ii) the case/pallet count is
                correct, and (iii) the freight is properly loaded and secured for transportation.
                Carrier shall note any exceptions on the freight receipt at time of loading, and
                shall also contact Transfix at (646) 844-2200 with details of such exceptions prior
                to transporting the freight. In the absence of such notations and contact, the
                freight will be deemed in good order and condition. In the event Carrier is
                prevented from loading, or observing loading and counting all freight on any
                shipment for any reason, then Carrier must immediately contact Transfix and notify
                Transfix of same, and only move the shipment upon Transfix&#39;s instructions to do
                so. Failure by Carrier to provide such notification or to move the shipment prior to
                receiving Transfix&#39;s instructions shall cause the shipment in question to be
                deemed driver&#39;s load and count.
              </li>
              <li>
                Carrier shall be solely responsible to set and maintain the internal temperature of
                its trailers to meet the requirements of the specific shipments hereunder,
                including, without limit, &#34;pre-cooling&#34; trailers and setting the trailers
                in-transit temperature maintenance settings based on supplier temperature
                maintenance specifications as are set forth on the shipper&#39;s bill of lading or
                freight receipt. Carrier shall contact Transfix&#39;s dispatching office via
                telephone in regard to any trailer ambient temperature variance of 2 degrees
                Fahrenheit from the temperature specifications on the shipper&#39;s bill of lading
                or freight receipt at any time while product is in transit. Carrier shall
                immediately contact Transfix&#39;s dispatching office via telephone if shipper does
                not provide temperature maintenance requirements on the bill of lading or freight
                receipt. [FOR REFRIGERATED SHIPMENTS ONLY]
              </li>
              <li>
                Carrier shall assure that the shipper has placed a Ryan recorder, TempTale or
                similar temperature recording device with all shipments. For partial truckload
                shipments, temperature recorders shall be placed by the shipper on top of a case on
                a pallet that is loaded near a side wall away from any direct discharge of
                refrigerated air. For full truckload shipments, the shipper shall place the
                temperature recorder on top of a case, near a side wall, one third of the way in
                from the rear doors, away from any direct discharge of refrigerated air. Carrier
                shall immediately contact Transfix&#39;s dispatching office via telephone in regard
                to any shipments or trailers for which temperatures recording devices have not been
                placed or have been improperly placed, and shall not transport the shipment in
                question without the prior approval of Transfix to do so. [FOR REFRIGERATED
                SHIPMENTS ONLY]
              </li>
              <li>
                The terms and conditions of Transfix&#39;s Load Confirmation are included by
                reference herein. In the event of conflict between any terms and conditions of any
                Load Confirmation and this Agreement, the Agreement shall govern.
              </li>
            </ol>
          </li>
          <li>
            Loads shall not be considered as &#34;Shipper Load and Count&#34; unless such condition
            is specifically noted on the freight receipt by the shipper, and signed by same. If
            proper shipper certification, as set forth in this Item 6, is not provided, Carrier
            agrees the trailer will be considered by the parties to have been loaded by carrier.
          </li>
          <li>
            In the event of Exceptions, including, but not limited to, temperature related damage to
            product, are discovered upon delivery the Parties shall work together to effect the
            salvage of the product, subject to the following:
            <ol type='a'>
              <li>
                Exception product may only be salvaged when Customer agrees to Carrier&#39;s doing
                so in writing.
              </li>
              <li>
                Except as set forth in the Agreement, in the event Exception product is salvaged,
                the proceeds of such salvage shall be remitted directly to Transfix as a means of
                both Parties&#39; meeting their obligations of mitigating claims, and any claims
                filed by Transfix shall be reduced in the amount of such salvage proceeds.
              </li>
              <li>
                Private label product may not be salvaged by Carrier under such private label name,
                and can only be salvaged upon Carrier providing Transfix with a certification and
                guarantee that the product will not be salvaged under Customers&#39; private label
                name or label, and will be clearly, boldly and obviously marked as
                &#34;Salvage,&#34; and Customers providing Transfix and Carrier with written salvage
                approval. In the event Customer does not provide its approval or Carrier fails to
                provide a certification and guarantee, the Exception product will be disposed of at
                Transfix&#39;s or Customer&#39;s discretion and no salvage value will be deducted
                from the full claim amount.
              </li>
              <li>
                Any product that would be deemed as contaminated under United States Food and Drug
                Administration regulations, shall be deemed damaged and unsalvageable, whether or
                not such contamination is proven, and if such condition was in whole or in part
                caused by Carrier, will be disposed of by Transfix or Customer at Carrier&#39;s cost
                and expense. In the event such condition(s) was caused by a shipper, Customers shall
                refuse such shipment(s), and Carrier shall work with Transfix and the shipper to
                effect disposal of the contaminated product, and Transfix and Customer shall have no
                liability for such product(s) or any costs related thereto.
              </li>
            </ol>
          </li>
          <li>
            Transfix shall notify Carrier, via telephone, facsimile, EDI, e-mail, or agreed upon
            website, transmission of the existence and availability of shipments. (&#34;Notice&#34;)
          </li>
          <li>
            Upon receipt of Notice, Carrier shall timely notify Transfix, via facsimile, telephone,
            EDI, e-mail, or agreed upon website transmission as to its acceptance or refusal to
            transport the shipment.
          </li>
          <li>
            Carrier, at origin, shall have the shipper designate or segregate the amount of freight
            to be delivered to any and all stop-off locations.
          </li>
          <li>
            Carrier&#39;s drivers shall insure that the proper freight is picked up from any and all
            shippers on multi-stop pick-ups, and that the freight picked up at each stop is properly
            secured for transportation to the next pick-up. Carrier&#39;s drivers shall insure that
            the proper freight is delivered to any and all stop-off locations on multi-stop
            deliveries, and that any freight remaining on the trailer is properly secured for
            continuation of transportation to another intermediate or the final delivery.
          </li>
          <li>
            Carrier will expedite all efforts to reconsign or divert a shipment in transit within
            one (1) hour of notification of same from Transfix.
          </li>
          <li>
            Carrier will not supply any equipment supplied under this Agreement that is or has been
            utilized for hauling any hazardous materials, refuse, or non-food products, as described
            in the Solid Waste Disposal Act, 42 U.S.C. § 6903, et. seq., the Sanitary Food
            Transportation Act, 49 U.S.C. § 5701, et seq. and/or the Food Safety Modernization Act.
          </li>
          <li>
            Carrier shall maintain the external and internal appearance of its vehicles in a
            suitably clean condition for appearance, product quality and condition, and safety,
            including, but not limited to being free from odors, leaks and sources of infestation
            and/or contamination. For refrigerated loads, carrier agrees that their equipment is
            maintained as per manufactures specification and is in working order.
          </li>
          <li>
            Carrier shall be solely responsible for any and all environmental claims of any type or
            kind, and for the investigation, remediation and/or clean-up that may arise from its
            operations under this Agreement, and any costs and expenses related thereto, except if
            caused by the negligence or willful misconduct of Transfix or Customer. Carrier
            represents and warrants that its responsibilities under this Agreement include
            familiarity and compliance with all hazardous materials regulations relevant to its
            operations, and the services it provides hereunder.
          </li>
          <li>
            Carrier shall not display or otherwise utilize Transfix&#39;s or Customers&#39; name,
            trademarks, logos, or other intellectual property on its equipment or materials,
            including advertising and marketing materials.
          </li>
          <li>
            Carrier agrees, where applicable, to exchange pallets with shippers, Customers, or their
            designated consignees, on a one-for-one basis. Any pallet balances caused by Carrier are
            Carriers sole responsibility. Unless otherwise expressly specified, all pallets are to
            be 4 way, 40&#34; x 48&#34; GMA hardboard pallets.
          </li>
          <li>
            Carrier shall submit a copy of Transfix&#39;s Load Confirmation with all other required
            shipping documents, e.g., POD&#39;s, receiving reports, packing slips when submitting
            Invoices to Transfix within thirty (30) days of the delivery date, or as otherwise
            specified on the rate confirmation, in order to receive payment.
          </li>
          <li>
            Upon being rated, Carrier shall at all times during the Term, maintain a FMCSA safety
            rating of &#34;Satisfactory,&#34; or &#34;Un-rated&#34; and will notify Transfix
            immediately in writing if its safety rating is changed to &#34;Conditional&#34; or
            &#34;Unsatisfactory.&#34; Carrier represents that it will at all times during the Term
            operate without FMCSA intervention or restriction. Carrier shall endeavor to maintain a
            satisfactory U.S. DOT Safety Rating but under no circumstances is Carrier allowed to
            provide services under this contract if their safety rating falls to
            &#34;unsatisfactory.&#34; Further, at the request of Transfix, the carrier will present
            a &#34;Safety Letter&#34; for its safety rating.
          </li>
          <li>
            In the event of any conflict between the terms and conditions of the Agreement and those
            found in this Appendix 1, the terms and conditions of this Appendix 1 shall apply.
          </li>
        </ol>
        <p className='tc'>APPENDIX 2</p>
        <p>Place accessorials and surcharges, including any fuel surcharge, here.</p>
      </Card>
    </>
  )
}
